import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

/** Patterns used to detect billing-related errors from backend gRPC responses. */
export const BILLING_ERROR_PATTERNS = {
  INSUFFICIENT_BALANCE: 'Insufficient balance',
  PROFILE_MALFORMED: 'BILLING_PROFILE_MALFORMED',
  RECONCILIATION_FAILED: 'BILLING_RECONCILIATION_FAILED',
  LEGACY_RECONCILIATION_FAILED: 'Billing reconciliation failed',
  FAILED_PRECONDITION: 'FAILED_PRECONDITION',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
} as const;

/**
 * Checks whether an error message indicates a billing/balance problem.
 */
export function isBillingError(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes(BILLING_ERROR_PATTERNS.INSUFFICIENT_BALANCE.toLowerCase());
}

/** Returns true for deterministic wallet-integrity failures shared by every child in a batch. */
export function isOperationWideBillingFailure(error: unknown): boolean {
  const candidate = error as { message?: unknown } | null;
  const message =
    error instanceof Error
      ? error.message
      : typeof candidate?.message === 'string'
        ? candidate.message
        : String(error ?? '');
  return (
    message.includes(BILLING_ERROR_PATTERNS.PROFILE_MALFORMED) ||
    message.includes(BILLING_ERROR_PATTERNS.RECONCILIATION_FAILED) ||
    message.includes(BILLING_ERROR_PATTERNS.LEGACY_RECONCILIATION_FAILED)
  );
}

/**
 * Extracts a user-facing error detail from one or more backend error messages.
 * Scans all messages and prioritizes known user-facing cases (billing, precondition,
 * auth) before falling back to the first non-empty message. Returns null only when
 * all messages are empty.
 */
export function extractUserFacingErrorDetail(errorMessages: string[]): string | null {
  if (!errorMessages || errorMessages.length === 0) return null;

  const trimmed = errorMessages.map(m => (m ?? '').trim()).filter(m => m.length > 0);

  if (trimmed.length === 0) return null;

  for (const msg of trimmed) {
    if (msg.includes(BILLING_ERROR_PATTERNS.INSUFFICIENT_BALANCE)) {
      return 'Insufficient balance to start new task. Please top up your account.';
    }
  }

  for (const msg of trimmed) {
    if (msg.includes(BILLING_ERROR_PATTERNS.FAILED_PRECONDITION)) {
      const descMatch = msg.match(/FAILED_PRECONDITION:\s*(.+)/);
      if (descMatch) return descMatch[1].trim();
      return msg;
    }
  }

  for (const msg of trimmed) {
    if (
      msg.includes(BILLING_ERROR_PATTERNS.PERMISSION_DENIED) ||
      msg.includes(BILLING_ERROR_PATTERNS.UNAUTHENTICATED)
    ) {
      return 'You do not have permission to perform this action. Please log in and try again.';
    }
  }

  return trimmed[0];
}

/**
 * Shows an error snackbar with billing-aware UX: if the error is billing-related,
 * the action button navigates to the billing page.
 */
export function showBillingAwareErrorSnackbar(
  snackBar: MatSnackBar,
  router: Router,
  message: string,
  durationMs = 10000
): void {
  const billing = isBillingError(message);
  const action = billing ? 'Go to Billing' : 'Dismiss';
  const ref = snackBar.open(message, action, {
    duration: durationMs,
    panelClass: billing ? ['error-snackbar'] : undefined,
  });
  if (billing) {
    ref.onAction().subscribe(() => router.navigate(['/billing']));
  }
}
